// Persistent-agent FILESYSTEMS — named, shareable file containers ("Filesystem"
// in the UI). A filesystem is attached to persistent-agent groups (many groups
// may share one); at agent-build time the host mounts it into the agent's
// Mastra Workspace as its WorkspaceFilesystem, which auto-injects the
// `workspace_*` file tools (read/write/edit/list/stat/mkdir/delete/grep).
//
// Vocabulary: the UI says "Filesystem" (the storable entity); "Workspace" is
// reserved for Mastra's runtime bundle assembled around it at build time.
// Text files only in v1; limits below are enforced by the filesystem layer
// (agent tools) AND the API (console edits) — keep them in one place here.
import { z } from 'zod';
// ─── Limits (shared by mastra-layer + api) ────────────────────────────────
/** Max bytes per file. LLM-oriented text files — not blob storage. */
export const persistentAgentFilesystemMaxFileBytes = 512 * 1024;
/** Max files per filesystem. */
export const persistentAgentFilesystemMaxFiles = 2_000;
/** Max path length (normalized POSIX, no leading slash). */
export const persistentAgentFilesystemMaxPathLength = 500;
// ─── Path + MIME helpers (shared by mastra-layer, api, console) ───────────
/**
 * Normalize a filesystem path to its stored form: POSIX separators, no
 * leading/trailing slashes, `.` segments dropped. Returns null when the path
 * tries to traverse upward (`..`) — there is nothing above the root.
 */
export function normalizePersistentAgentFilesystemPath(raw) {
    const cleaned = raw.replace(/\\/g, '/').trim();
    const segments = [];
    for (const segment of cleaned.split('/')) {
        if (segment === '' || segment === '.')
            continue;
        if (segment === '..')
            return null;
        segments.push(segment);
    }
    return segments.join('/');
}
/** MIME by extension — enough for the console's "render markdown" case. */
const filesystemMimeByExtension = {
    md: 'text/markdown',
    markdown: 'text/markdown',
    json: 'application/json',
    yaml: 'application/yaml',
    yml: 'application/yaml',
    csv: 'text/csv',
    html: 'text/html',
    css: 'text/css',
    js: 'text/javascript',
    ts: 'text/typescript',
    tsx: 'text/typescript',
    py: 'text/x-python',
    txt: 'text/plain',
};
export function persistentAgentFilesystemMimeType(path) {
    const dot = path.lastIndexOf('.');
    if (dot === -1 || dot === path.length - 1)
        return 'text/plain';
    return filesystemMimeByExtension[path.slice(dot + 1).toLowerCase()] ?? 'text/plain';
}
// ─── Entities ──────────────────────────────────────────────────────────────
/** The filesystem row, as persisted. */
export const PersistentAgentFilesystemSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/** A group that has this filesystem attached (for "shared by" displays). */
export const PersistentAgentFilesystemGroupRefSchema = z.object({
    id: z.string(),
    name: z.string(),
});
/** List/detail projection: the row plus usage facts the console shows. */
export const PersistentAgentFilesystemDetailSchema = PersistentAgentFilesystemSchema.extend({
    /** Number of file rows (dirs excluded). */
    fileCount: z.number().int().nonnegative(),
    /** Total content bytes across files. */
    totalBytes: z.number().int().nonnegative(),
    /** Live groups this filesystem is attached to. */
    attachedGroups: z.array(PersistentAgentFilesystemGroupRefSchema),
});
/**
 * One entry of the flat file listing (`GET /:id/files`). The console builds
 * the tree client-side from paths; explicitly-created empty directories
 * appear as `type: 'dir'` rows.
 */
export const PersistentAgentFilesystemEntrySchema = z.object({
    path: z.string(),
    type: z.enum(['file', 'dir']),
    size: z.number().int().nonnegative(),
    mimeType: z.string().nullable(),
    updatedAt: z.date(),
});
/** Full file content (`GET /:id/file?path=…`). */
export const PersistentAgentFilesystemFileSchema = PersistentAgentFilesystemEntrySchema.extend({
    content: z.string(),
    /** Last writer — 'agent' (a thread id) or 'user' (an auth subject). */
    updatedByType: z.string().nullable(),
    updatedById: z.string().nullable(),
});
// ─── HTTP surface ──────────────────────────────────────────────────────────
export const CreatePersistentAgentFilesystemInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
});
export const UpdatePersistentAgentFilesystemInputSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    /** null clears the description; undefined leaves unchanged. */
    description: z.string().max(2000).nullable().optional(),
});
/**
 * Body for `PUT /:id/file` — create or overwrite one file from the console.
 * The path is normalized server-side (leading slashes stripped, `..` rejected).
 */
export const PutPersistentAgentFilesystemFileInputSchema = z.object({
    path: z.string().min(1).max(persistentAgentFilesystemMaxPathLength),
    content: z.string().max(persistentAgentFilesystemMaxFileBytes),
});
//# sourceMappingURL=persistent-agent-filesystem.js.map